import { useEffect } from "react";
import { SITE_URL, type RouteMeta } from "../lib/seoMeta";

function upsertMeta(name: string, content: string | undefined): void {
  if (content == null) return; // nothing to set → keep whatever is there
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href: string | null): void {
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (href == null) {
    // noindex routes get no canonical — combining noindex with rel=canonical
    // sends conflicting signals (Google's own guidance is to not pair them).
    existing?.remove();
    return;
  }
  let el = existing;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function normalizePath(path: string): string {
  if (!path || path === "/") return "/";
  const clean = ("/" + path.replace(/^\/+/, "")).replace(/\/+$/, "");
  return clean || "/";
}

/**
 * Per-route document metadata for search engines. Imperatively updates the
 * <title>, description, robots and canonical tags that ship statically in
 * index.html (mutating the existing tags rather than appending duplicates).
 *
 * Runs client-side only, so it benefits JS-rendering crawlers (Google).
 * Non-JS crawlers keep index.html's static homepage tags + canonical → / and
 * simply consolidate every route to the homepage, which is harmless.
 */
export function useDocumentMeta(meta: RouteMeta): void {
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.title = meta.title;
    upsertMeta("description", meta.description);
    upsertMeta("robots", `${meta.index ? "index,follow" : "noindex,follow"},max-image-preview:large`);
    setCanonical(meta.index ? SITE_URL + normalizePath(meta.path) : null);
  }, [meta.title, meta.description, meta.index, meta.path]);
}
